import type { OperationManifest, ConnectorInfo } from '@/types/operationManifest';
import { SettingScope } from '@/types/operationManifest';
import { frequencyValues } from './schedule';

import { timeZones } from '@/data/timeZones';
export { timeZones };

const dateTimeFormats = [
  {
    value: 'd',
    displayName: 'Short date pattern - 6/15/2009 [d]',
  },
  {
    value: 'D',
    displayName: 'Long date pattern - Monday, June 15, 2009 [D]',
  },
  {
    value: 'f',
    displayName: 'Full date/time pattern (short time) - Monday, June 15, 2009 1:45 PM [f]',
  },
  {
    value: 'F',
    displayName: 'Full date/time pattern (long time) - Monday, June 15, 2009 1:45:30 PM [F]',
  },
  {
    value: 'g',
    displayName: 'General date/time pattern (short time) - 6/15/2009 1:45 PM [g]',
  },
  {
    value: 'G',
    displayName: 'General date/time pattern (long time) - 6/15/2009 1:45:30 PM [G]',
  },
  {
    value: 'm',
    displayName: 'Month/day pattern - June 15 [m]',
  },
  {
    value: 'o',
    displayName: 'Round-trip date/time pattern - 2009-06-15T13:45:30.0000000-07:00 [o]',
  },
  {
    value: 'r',
    displayName: 'RFC1123 pattern - Mon, 15 Jun 2009 20:45:30 GMT [r]',
  },
  {
    value: 's',
    displayName: 'Sortable date/time pattern - 2009-06-15T13:45:30 [s]',
  },
  {
    value: 't',
    displayName: 'Short time pattern - 1:45 PM [t]',
  },
  {
    value: 'T',
    displayName: 'Long time pattern - 1:45:30 PM [T]',
  },
  {
    value: 'u',
    displayName: 'Universal sortable date/time pattern - 2009-06-15 13:45:30Z [u]',
  },
  {
    value: 'U',
    displayName: 'Universal full date/time pattern - Monday, June 15, 2009 8:45:30 PM [U]',
  },
  {
    value: 'y',
    displayName: 'Year month pattern - June, 2009 [y]',
  },
];

const dateTimeConnector: ConnectorInfo = {
  id: 'connectionProviders/datetime',
  name: 'datetime',
  properties: {
    description: 'Date Time operations',
    displayName: 'Date Time',
  },
};

const dateTimeIcon =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZlcnNpb249IjEuMSIgdmlld0JveD0iMCAwIDMyIDMyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPg0KIDxwYXRoIGQ9Im0wIDBoMzJ2MzJoLTMyeiIgZmlsbD0iIzFGODVGRiIvPg0KIDxnIGZpbGw9IiNmZmYiPg0KICA8cGF0aCBkPSJNMTYuMDE1IDEwLjEzOGMtMy44NC0uMDA3LTYuOTUzIDMuMTA2LTYuOTQ2IDYuOTMxLjAwNyAzLjgzMyAzLjEwNiA2LjkzMSA2LjkyNCA2LjkzMSAzLjgxOCAwIDYuOTMxLTMuMTA2IDYuOTM4LTYuOTI0LjAwNy0zLjgxMS0zLjEwNi02LjkzOC02LjkxNy02LjkzOHptLS4wMjkgMTIuMzI4Yy0zLjA3Ni0uMDE1LTUuMzk2LTIuNDk1LTUuMzgyLTUuNDE4LjAxNS0zLjA5OCAyLjU5Ni01LjQ2MiA1LjYwNy01LjM3NSAyLjgxNS4wODcgNS4yMDcgMi41MzEgNS4xNzggNS4zOTYtLjAwNyAyLjk4OS0yLjM5MyA1LjQxMS01LjQwNCA1LjM5NnoiLz4NCiAgPHBhdGggZD0iTTIxLjk0OSAxMi4xMzhjLjEzOC0uMzQ5LjI2Mi0uNjkxLjQxNS0xLjAxOC4yNC0uNTAyLjEwOS0uOTM4LS4yNjItMS4yODctLjg4LS44MzYtMS45MTMtMS40MzMtMy4wODQtMS43NjctLjM2NC0uMTAyLS43NjQtLjExNi0xLjA0LjE5Ni0uMzEzLjM0OS0uNTc1Ljc0OS0uODczIDEuMTQ5IDEuOTQyLjM1NiAzLjUyNyAxLjI4IDQuODQ0IDIuNzI3eiIvPg0KICA8cGF0aCBkPSJNMTAuMDI5IDEyLjExNmMxLjMwOS0xLjQ2MiAyLjg5NS0yLjM2NCA0LjgxNS0yLjcyNy0uMjMzLS4zMjctLjQ1OC0uNjE4LS42NTUtLjkxNi0uMjY5LS40MjItLjY1NS0uNTMxLTEuMTEzLS40NDQtMS4wNC4yMDQtMS45MTMuNzU2LTIuNzU2IDEuMzY3LS4xODIuMTMxLS4zNDkuMjk4LS41MDIuNDY1LS4yNjIuMjg0LS40MDcuNjE4LS4yOTEuOTk2LjEzOC40MTUuMzI3LjgyMi41MDIgMS4yNTh6Ii8+DQogIDxwYXRoIGQ9Ik0xNi41MTYgMTIuOTIzYy0uNjA0LS4zMjctMS4yNjUuMDUxLTEuMjY1LjczNS4wMDcuOTY3LjAxNSAxLjkzNS4wMzYgMi45MDkuMDA3LjE4OS0uMDQ0LjMyNy0uMTk2LjQ1OC0uMzg1LjMyLS43NzEuNjQ3LTEuMTM1Ljk4OS0uMzEzLjI5MS0uMzA1LjczNS0uMDE1IDEuMDU1LjI4NC4zMTMuNzQyLjM2NCAxLjA1NS4wOTUuNTM4LS40NjUgMS4wNjktLjkzMSAxLjU4NS0xLjQxOC4xMDktLjEwMi4yMTEtLjI5MS4yMTEtLjQzNi4wMjItLjY3Ni4wMDctMS4zNi4wMDctMi4wMzZoLjAzNnYtMS43MzFjLjAwNy0uMjY5LS4wNzMtLjQ4LS4zMi0uNjE4eiIvPg0KIDwvZz4NCjwvc3ZnPg0K';
const dateTimeBrandColor = '#1F85FF';

export const addToTimeManifest = {
  properties: {
    iconUri: dateTimeIcon,
    brandColor: dateTimeBrandColor,
    description: 'Adds a time span to a specified time.',
    summary: 'Add to time',

    inputs: {
      type: 'object',
      properties: {
        baseTime: {
          type: 'string',
          title: 'Base time',
          description: 'The time to add the interval to.',
        },
        interval: {
          type: 'integer',
          title: 'Interval',
          description: 'Specify the interval of time to add to the base time',
        },
        timeUnit: {
          type: 'string',
          title: 'Time unit',
          description: 'The unit of time the interval is specified in.',
          'x-ms-editor': 'combobox',
          'x-ms-editor-options': {
            options: frequencyValues,
          },
          default: 'Hour',
        },
      },
      required: ['baseTime', 'interval', 'timeUnit'],
    },
    inputsLocation: ['inputs'],

    outputs: {
      type: 'object',
      required: ['body'],
      properties: {
        body: {
          type: 'string',
          title: 'Calculated time',
        },
      },
    },
    isOutputsOptional: false,

    connector: dateTimeConnector,

    settings: {
      secureData: {},
      trackedProperties: {
        scopes: [SettingScope.Action],
      },
    },
  },
} as OperationManifest;

export const convertTimezoneManifest = {
  properties: {
    iconUri: dateTimeIcon,
    brandColor: dateTimeBrandColor,
    description: 'Converts a time to a specified target time zone',
    summary: 'Convert time zone',

    inputs: {
      type: 'object',
      properties: {
        baseTime: {
          type: 'string',
          title: 'Base time',
          description: 'The time to convert.',
        },
        sourceTimeZone: {
          type: 'string',
          title: 'Source time zone',
          description: 'The time zone the base time is in.',
          'x-ms-editor': 'combobox',
          'x-ms-editor-options': {
            options: timeZones,
          },
          default: 'UTC',
        },
        destinationTimeZone: {
          type: 'string',
          title: 'Destination time zone',
          description: 'The time zone to convert to.',
          'x-ms-editor': 'combobox',
          'x-ms-editor-options': {
            options: timeZones,
          },
          default: 'UTC',
        },
        formatString: {
          type: 'string',
          title: 'Time unit',
          description: 'A string specifying the desired format of the converted time.',
          'x-ms-visibility': 'important',
          'x-ms-editor': 'combobox',
          'x-ms-editor-options': {
            options: dateTimeFormats,
          },
          default: 'd',
        },
      },
      required: ['baseTime', 'sourceTimeZone', 'destinationTimeZone'],
    },
    inputsLocation: ['inputs'],

    outputs: {
      type: 'object',
      required: ['body'],
      properties: {
        body: {
          type: 'string',
          title: 'Converted time',
        },
      },
    },
    isOutputsOptional: false,

    connector: dateTimeConnector,

    settings: {
      secureData: {},
      trackedProperties: {
        scopes: [SettingScope.Action],
      },
    },
  },
} as OperationManifest;

export const currentTimeManifest = {
  properties: {
    iconUri: dateTimeIcon,
    brandColor: dateTimeBrandColor,
    description: 'Gets the current time in UTC.',
    summary: 'Current time',

    inputs: {
      'x-ms-visibility': 'hideInUI',
      default: {},
    },

    outputs: {
      type: 'object',
      required: ['body'],
      properties: {
        body: {
          type: 'string',
          title: 'Current time',
        },
      },
    },
    isOutputsOptional: false,

    connector: dateTimeConnector,

    settings: {
      secureData: {},
      trackedProperties: {
        scopes: [SettingScope.Action],
      },
    },
  },
} as OperationManifest;

export const getFutureTimeManifest = {
  properties: {
    iconUri: dateTimeIcon,
    brandColor: dateTimeBrandColor,
    description: 'Returns a timestamp that is the current time plus the specified time interval.',
    summary: 'Get future time',

    inputs: {
      type: 'object',
      properties: {
        interval: {
          type: 'integer',
          title: 'Interval',
          description: 'Specify the time interval between the current time and the desired future time.',
        },
        timeUnit: {
          type: 'string',
          title: 'Time unit',
          description: 'The unit of time the interval is specified in.',
          'x-ms-editor': 'combobox',
          'x-ms-editor-options': {
            options: frequencyValues,
          },
          default: 'Hour',
        },
      },
      required: ['interval', 'timeUnit'],
    },
    inputsLocation: ['inputs'],

    outputs: {
      type: 'object',
      required: ['body'],
      properties: {
        body: {
          type: 'string',
          title: 'Future time',
        },
      },
    },
    isOutputsOptional: false,

    connector: dateTimeConnector,

    settings: {
      secureData: {},
      trackedProperties: {
        scopes: [SettingScope.Action],
      },
    },
  },
} as OperationManifest;

export const getPastTimeManifest = {
  properties: {
    iconUri: dateTimeIcon,
    brandColor: dateTimeBrandColor,
    description: 'Returns a timestamp that is the current time minus the specified time interval.',
    summary: 'Get past time',

    inputs: {
      type: 'object',
      properties: {
        interval: {
          type: 'integer',
          title: 'Interval',
          description: 'Specify the time interval between the current time and the desired time in the past.',
        },
        timeUnit: {
          type: 'string',
          title: 'Time unit',
          description: 'The unit of time the interval is specified in.',
          'x-ms-editor': 'combobox',
          'x-ms-editor-options': {
            options: frequencyValues,
          },
          default: 'Hour',
        },
      },
      required: ['interval', 'timeUnit'],
    },
    inputsLocation: ['inputs'],

    outputs: {
      type: 'object',
      required: ['body'],
      properties: {
        body: {
          type: 'string',
          title: 'Past time',
        },
      },
    },
    isOutputsOptional: false,

    connector: dateTimeConnector,

    settings: {
      secureData: {},
      trackedProperties: {
        scopes: [SettingScope.Action],
      },
    },
  },
} as OperationManifest;

export const subtractFromTimeManifest = {
  properties: {
    iconUri: dateTimeIcon,
    brandColor: dateTimeBrandColor,
    description: 'Subtracts a time span from a specified time.',
    summary: 'Subtract from time',

    inputs: {
      type: 'object',
      properties: {
        baseTime: {
          type: 'string',
          title: 'Base time',
          description: 'The time to subtract the interval from.',
        },
        interval: {
          type: 'integer',
          title: 'Interval',
          description: 'Specify the interval of time to subtract from the base time.',
        },
        timeUnit: {
          type: 'string',
          title: 'Time unit',
          description: 'The unit of time the interval is specified in.',
          'x-ms-editor': 'combobox',
          'x-ms-editor-options': {
            options: frequencyValues,
          },
          default: 'Hour',
        },
      },
      required: ['baseTime', 'interval', 'timeUnit'],
    },
    inputsLocation: ['inputs'],

    outputs: {
      type: 'object',
      required: ['body'],
      properties: {
        body: {
          type: 'string',
          title: 'Calculated time',
        },
      },
    },
    isOutputsOptional: false,

    connector: dateTimeConnector,

    settings: {
      secureData: {},
      trackedProperties: {
        scopes: [SettingScope.Action],
      },
    },
  },
} as OperationManifest;
